/**
 * Building an email the way Gmail builds one.
 *
 * Kyu, 3 September: "We just want to make sure the email formats perfectly so
 * no one can notice the email came from a Hub and not his gmail."
 *
 * That is a precise requirement and it decides everything in this file. The
 * Hub is not sending "an email" — it is sending the email Gmail's compose
 * window would have sent had Alex typed the same words. So each function here
 * reproduces one thing Gmail does, and the comment above each says what and
 * why.
 *
 * ── The block between the sentinels is copied verbatim into the Edge Function
 * `supabase/functions/send-as-mailbox/index.ts`, which cannot import from
 * src/. mailSendContract.test.ts holds the two copies identical. Anything
 * outside the sentinels is browser-only and is not copied.
 */

// ── shared mail-send rules: keep identical with supabase/functions/send-as-mailbox ──

/**
 * The characters HTML gives meaning to. Everything the person typed is text,
 * and text with an `&` or a `<` in it has to arrive as that character rather
 * than as markup. Without this "Hey <name>" silently disappears — a browser
 * reads it as an unknown tag — which is worse than showing it, because nobody
 * sees that it went wrong.
 */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Bare URLs become links, the way Gmail makes them on paste.
 *
 * Runs on ALREADY-ESCAPED text, so `&` inside a URL is `&amp;` here — which is
 * the correct spelling inside an HTML attribute. Trailing punctuation is left
 * outside the link: "see https://4ocean.com." should link the site, not the
 * full stop.
 */
export function linkify(escaped: string): string {
  return escaped.replace(/https?:\/\/[^\s<]+/g, (match) => {
    const trimmed = match.replace(/[.,;:!?)]+$/, '');
    const tail = match.slice(trimmed.length);
    return `<a href="${trimmed}">${trimmed}</a>${tail}`;
  });
}

/**
 * Placeholders nobody filled in.
 *
 * The copy library uses `{{first_name}}`; older copy still carries `<name>`,
 * and a `[company]` from a paste would look just as plausible. Any of them
 * reaching a prospect under the CEO's name is the one mistake this feature
 * cannot make once, so the send path REFUSES rather than guesses. Returned as
 * a list so the message can say which.
 */
export function unresolvedPlaceholders(text: string): string[] {
  const found = new Set<string>();
  for (const match of text.matchAll(/\{\{[^}]*\}\}|<[a-z][a-z _-]*>|\[[a-z][a-z _-]*\]/gi)) {
    found.add(match[0]);
  }
  return [...found];
}

/**
 * Formatting the team writes by hand: a few marks in plain text, rendered into
 * exactly the HTML Gmail's compose window would have produced for the same
 * formatting. Chosen over a rich editor that stores HTML because this shape
 * cannot produce an email that looks "sent by software" and that one can
 * (docs/rich-email-design-note.md).
 *
 *   **bold**   _italic_   __underline__   [words](https://…)
 *   - a bullet line (also "* " and "• ")
 *
 * Kyu, 3 September: "bold, italic, underline. bullet points too, Trebuchet
 * and worksans for font". Kyu, 7 September, closing the question of whether a
 * Hub email should match Alex's Gmail default instead: "always Trebuchet". So
 * Trebuchet is first and Work Sans is gone — a reader who happened to have
 * Work Sans installed would otherwise have seen it instead of Trebuchet. The
 * rest of the stack is what an email can promise, which is a fallback: a
 * reader without Trebuchet gets Helvetica or Arial, then their sans-serif.
 *
 * Order matters and is fixed: the text is escaped first (so nothing typed is
 * ever markup), then links and bare URLs are set aside so an underscore in a
 * URL is never read as italics, then the inline marks, then the URLs go back
 * as anchors.
 *
 * A body with none of these marks is the common case, and it must keep
 * rendering as it did before this existed (plus the font on the outer div).
 * So the rules are narrow on purpose: an underscore only opens italics at a
 * word boundary ("first_name" is not italics), a lone asterisk is an
 * asterisk, and a bracket not followed by "(url)" is just a bracket — and
 * still caught by unresolvedPlaceholders.
 */
export const EMAIL_FONT_STACK = "'Trebuchet MS', Helvetica, Arial, sans-serif";

/** Only these may be the target of a written link. `javascript:` is not a link. */
const LINK_SCHEME = /^(https?:\/\/|mailto:)/i;

/**
 * A photo's caption, with the size the writer chose split off it.
 *
 * Hanneza (3 Sep): the August nurture photos were sent from Gmail at a chosen
 * size, and a photo the Hub sends at its own size might land too big. So the
 * Photo size choice is written into the mark as `![caption|480](…)` — the
 * caption, a bar, the width in pixels. No bar, or anything after it that is
 * not a whole number, means "its own size" and the bar stays part of the
 * caption. Widths outside 80–1200 are ignored the same way: a typo cannot
 * make a one-pixel or a ten-thousand-pixel picture.
 */
export function photoCaption(raw: string): { alt: string; width: number | null } {
  const match = /^(.*)\|(\d{2,4})$/.exec(raw);
  if (match !== null) {
    const width = Number(match[2]);
    if (width >= 80 && width <= 1200) return { alt: (match[1] ?? '').trim(), width };
  }
  return { alt: raw.trim(), width: null };
}

/** "- item", "* item" or "• item" — the line is a bullet, and this is its text. */
export function bulletText(line: string): string | null {
  const match = /^\s*[-*•]\s+(.*)$/.exec(line);
  return match === null ? null : (match[1] ?? '');
}

/**
 * Inline marks on ONE line of already-escaped text. Links and bare URLs are
 * lifted out first and put back at the end, so their insides are never
 * touched by the bold/italic/underline rules. The stand-in is an HTML comment,
 * which escaped text cannot contain.
 */
export function renderInlineMarks(escaped: string): string {
  const held: string[] = [];
  const hold = (html: string): string => {
    held.push(html);
    return `<!--k${String(held.length - 1)}-->`;
  };

  // ![caption](cid:…) — a photo that travels inside the email (drop B of
  // docs/rich-email-design-note.md). Only a cid: reference renders: it names
  // an inline part of THIS message. A photo: or drive: reference is a photo
  // the browser has not resolved yet, and is left as text so it can never go
  // out half-done; the send path refuses it first (unresolvedPhotos).
  //
  // A chosen size rides in the caption as `caption|480` (photoCaption). With
  // one, the picture is drawn at that width; without, at its own size. Both
  // are capped at the email's width so a large photo never overflows.
  let out = escaped.replace(
    /!\[([^[\]\n]*)\]\(cid:([A-Za-z0-9._@-]+)\)/g,
    (_whole: string, rawCaption: string, cid: string) => {
      const { alt, width } = photoCaption(rawCaption);
      return hold(
        width === null
          ? `<img src="cid:${cid}" alt="${alt}" style="max-width:100%">`
          : `<img src="cid:${cid}" alt="${alt}" width="${String(width)}" style="width:${String(width)}px;max-width:100%">`,
      );
    },
  );

  // [words](url) — the words may carry marks; the url may not.
  out = out.replace(
    /\[([^[\]\n]+)\]\(([^\s()]+)\)/g,
    (whole: string, words: string, url: string) =>
      LINK_SCHEME.test(url) ? hold(`<a href="${url}">${marksOnly(words)}</a>`) : whole,
  );
  // Bare URLs, as Gmail links them on paste. Trailing punctuation stays outside.
  out = out.replace(/https?:\/\/[^\s<]+/g, (match) => {
    const trimmed = match.replace(/[.,;:!?)]+$/, '');
    const tail = match.slice(trimmed.length);
    return hold(`<a href="${trimmed}">${trimmed}</a>`) + tail;
  });

  out = marksOnly(out);
  return out.replace(/<!--k(\d+)-->/g, (_m, index: string) => held[Number(index)] ?? '');
}

/** Bold, underline, italic — in that order, so `__` is read before `_`. */
function marksOnly(text: string): string {
  return text
    .replace(/\*\*(\S(?:[^*\n]*?\S)?)\*\*/g, '<b>$1</b>')
    .replace(/__(\S(?:[^_\n]*?\S)?)__/g, '<u>$1</u>')
    .replace(/(^|[^\w])_(\S(?:[^_\n]*?\S)?)_(?!\w)/g, '$1<i>$2</i>');
}

/**
 * The body as Gmail would have produced it: each line in its own `<div>`, a
 * blank line as `<div><br></div>`, consecutive bullet lines as one `<ul>`.
 */
export function renderBodyHtml(body: string): string {
  const out: string[] = [];
  let bullets: string[] = [];
  const flush = () => {
    if (bullets.length > 0) {
      out.push(`<ul>${bullets.map((item) => `<li>${item}</li>`).join('')}</ul>`);
      bullets = [];
    }
  };
  for (const line of body.split('\n')) {
    const bullet = bulletText(line);
    if (bullet !== null) {
      bullets.push(renderInlineMarks(escapeHtml(bullet)));
      continue;
    }
    flush();
    out.push(
      line.trim() === '' ? '<div><br></div>' : `<div>${renderInlineMarks(escapeHtml(line))}</div>`,
    );
  }
  flush();
  return out.join('');
}

/**
 * The same body with the marks taken off — the words a text-only reader gets.
 * A link keeps its address beside its words, because the words alone would
 * be a promise with nothing behind it. Also what the placeholder check reads,
 * so `[Lookbook](https://…)` is never mistaken for a `[placeholder]`.
 */
export function marksToText(body: string): string {
  return body
    .split('\n')
    .map((line) => {
      const bullet = bulletText(line);
      const text = bullet === null ? line : `- ${bullet}`;
      return text
        .replace(
          /!\[([^[\]\n]*)\]\((?:cid:[A-Za-z0-9._@-]+|(?:photo|drive):[^()\n]+)\)/g,
          (_whole: string, rawCaption: string) => {
            const { alt } = photoCaption(rawCaption);
            return alt === '' ? '[Photo]' : `[Photo: ${alt}]`;
          },
        )
        .replace(/\[([^[\]\n]+)\]\(([^\s()]+)\)/g, (whole: string, words: string, url: string) =>
          LINK_SCHEME.test(url) ? `${words} (${url})` : whole,
        )
        .replace(/\*\*(\S(?:[^*\n]*?\S)?)\*\*/g, '$1')
        .replace(/__(\S(?:[^_\n]*?\S)?)__/g, '$1')
        .replace(/(^|[^\w])_(\S(?:[^_\n]*?\S)?)_(?!\w)/g, '$1$2');
    })
    .join('\n');
}

/**
 * Photo references the browser has not turned into inline parts. A body may
 * only be sent when this is empty: `photo:` and `drive:` are the writer's
 * names for a picture, `cid:` is the message's. Returned as a list so the
 * message can say which.
 *
 * ── Why there are two passes ───────────────────────────────────────────────
 *
 * Hanneza, 3 Sep: a photo she picked arrived in the lead's inbox as the text
 * `![Screenshot 2026-09-03 at 11.51.09 PM](photo:Screenshot 2026-09-03 at
 * 11.51.09 PM.png)`. The reference pattern did not allow a SPACE, so a file
 * named the way a Mac names a screenshot matched nothing: the browser
 * attached no picture, and this function — which exists to stop exactly that
 * send — also saw nothing and let it through. One wrong character class
 * silently turned the guard off.
 *
 * So the first pass reads the mark properly, and the second pass is
 * deliberately dumber than the grammar: anything at all that looks like
 * `](photo:` or `](drive:` and was not already claimed by the first pass
 * stops the send. If the grammar is ever wrong again, the guard still fires.
 */
export function unresolvedPhotos(body: string): string[] {
  const found = new Set<string>();
  const claimed: [number, number][] = [];

  for (const match of body.matchAll(/!\[[^[\]\n]*\]\(((?:photo|drive):[^()\n]+)\)/g)) {
    found.add(match[1] ?? '');
    const at = match.index ?? 0;
    claimed.push([at, at + match[0].length]);
  }

  for (const match of body.matchAll(/\]\((photo|drive):([^\n]{0,200})/g)) {
    const at = match.index ?? 0;
    if (claimed.some(([start, end]) => at >= start && at < end)) continue;
    const rest = match[2] ?? '';
    const close = rest.indexOf(')');
    found.add(`${match[1] ?? ''}:${(close === -1 ? rest : rest.slice(0, close)).trim()}`);
  }

  return [...found];
}

/**
 * What Gmail's compose window produces for typed text.
 *
 * Checked against real Gmail output rather than assumed: an outer
 * `<div dir="ltr">`, each line in its own `<div>`, and a blank line as
 * `<div><br></div>`. The one thing added on purpose is the font, at Kyu's
 * request (EMAIL_FONT_STACK) — the same way Gmail writes a sender's chosen
 * default font onto the message. If Alex's Gmail is set to a different
 * default, that is the one visible difference between a Hub email and one he
 * typed. Kyu ruled on it, 7 September: the Hub does not follow the Gmail
 * default, it is always Trebuchet.
 *
 * Then the signature, as Gmail appends it: a blank line, then the signature
 * container carrying Gmail's own class and data attribute. The signature HTML
 * itself is Alex's, read live from his settings and passed in untouched.
 *
 * GMAIL_SIGNATURE_PREFIX is the "-- " Gmail places above a signature. It is
 * the one detail this file cannot verify without a real sample from Alex's
 * Sent folder, so it is a constant with a name, easy to flip after the
 * side-by-side test (docs/sales-inbox-design-note.md).
 */
export const GMAIL_SIGNATURE_PREFIX = true;

export function gmailHtml(body: string, signatureHtml: string): string {
  const lines = renderBodyHtml(body);

  const signature =
    signatureHtml.trim() === ''
      ? ''
      : '<div><br></div>' +
        (GMAIL_SIGNATURE_PREFIX ? '<span class="gmail_signature_prefix">-- </span><br>' : '') +
        `<div dir="ltr" class="gmail_signature" data-smartmail="gmail_signature">${signatureHtml}</div>`;

  return `<div dir="ltr" style="font-family:${EMAIL_FONT_STACK}">${lines}${signature}</div>`;
}

/**
 * The text/plain twin Gmail always sends alongside the HTML.
 *
 * A message with only an HTML part is a common "software sent this" signal,
 * to spam filters and to anyone who looks. The plain part carries the same
 * words with the marks taken off, and the signature with its tags stripped.
 */
export function gmailText(body: string, signatureHtml: string): string {
  const signatureText = signatureHtml
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(div|p|tr)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  const text = marksToText(body);
  if (signatureText === '') return text;
  return `${text}\n\n${GMAIL_SIGNATURE_PREFIX ? '-- \n' : ''}${signatureText}`;
}

export interface MailParts {
  /** "Alex Schulze <alexjschulze@4ocean.com>" — display name from Gmail's own sendAs record. */
  from: string;
  to: string;
  subject: string;
  text: string;
  html: string;
  /**
   * Set on a follow-up so it lands in the same conversation as the first
   * email, the way Alex's own follow-ups would. Both headers, because clients
   * disagree about which one they thread on.
   */
  inReplyTo?: string;
  references?: string;
  /** Files to carry. Kyu: attachments are "mostly in the next few emails". */
  attachments?: MailAttachmentPart[];
  /**
   * Optional, added for lead-first-touch's HubSpot BCC logging copy (brief
   * "HubSpot logging. BCC our HubSpot BCC logging address."). Unused by
   * send-as-mailbox today — every existing caller omits it, so this is a
   * pure addition, not a behaviour change for the send path already live.
   */
  bcc?: string;
}

export interface MailAttachmentPart {
  name: string;
  mimeType: string;
  bytes: Uint8Array;
  /**
   * Set on a photo that sits IN the body: the html refers to it as
   * `cid:<contentId>`, and the part is marked inline, the way Gmail sends a
   * pasted picture. Absent on an ordinary attachment.
   */
  contentId?: string;
}

/**
 * RFC 2822 text of the message, CRLF throughout.
 *
 * Without attachments: multipart/alternative, text part before html part —
 * exactly what Gmail's compose window sends. With attachments: multipart/mixed
 * wrapping that same alternative part, then one base64 part per file, which
 * is also exactly what Gmail sends when a file is dropped on a compose window.
 *
 * Subject and file names are RFC 2047-encoded when they hold anything outside
 * ASCII, which a company name with an accent will.
 */
export function buildRfc822(parts: MailParts): string {
  const boundary = `000000000000${Math.random().toString(16).slice(2, 14)}`;
  const headers = [
    `From: ${parts.from}`,
    `To: ${parts.to}`,
    ...(parts.bcc ? [`Bcc: ${parts.bcc}`] : []),
    `Subject: ${encodeHeader(parts.subject)}`,
    'MIME-Version: 1.0',
    ...(parts.inReplyTo ? [`In-Reply-To: ${parts.inReplyTo}`] : []),
    ...(parts.references ? [`References: ${parts.references}`] : []),
  ];

  const alternative = [
    `--${boundary}`,
    'Content-Type: text/plain; charset="UTF-8"',
    'Content-Transfer-Encoding: quoted-printable',
    '',
    quotedPrintable(parts.text),
    `--${boundary}`,
    'Content-Type: text/html; charset="UTF-8"',
    'Content-Transfer-Encoding: quoted-printable',
    '',
    quotedPrintable(parts.html),
    `--${boundary}--`,
  ];

  const all = parts.attachments ?? [];
  const inline = all.filter((file) => file.contentId !== undefined);
  const attached = all.filter((file) => file.contentId === undefined);

  // A quote inside a quoted parameter would end it early; a file name is
  // never worth a broken header.
  const safeName = (file: MailAttachmentPart) => encodeHeader(file.name.replace(/"/g, ''));

  // Photos in the body: multipart/related around the text/html pair, one
  // inline part per picture with the Content-ID the html points at. This is
  // the shape Gmail itself produces for a pasted picture, and the one every
  // mail client shows in place rather than as a paperclip.
  let content: string[];
  let contentType: string;
  if (inline.length === 0) {
    content = alternative;
    contentType = `multipart/alternative; boundary="${boundary}"`;
  } else {
    const related = `000000000000${Math.random().toString(16).slice(2, 14)}`;
    content = [
      `--${related}`,
      `Content-Type: multipart/alternative; boundary="${boundary}"`,
      '',
      ...alternative,
    ];
    for (const file of inline) {
      const name = safeName(file);
      content.push(
        `--${related}`,
        `Content-Type: ${file.mimeType || 'application/octet-stream'}; name="${name}"`,
        'Content-Transfer-Encoding: base64',
        `Content-ID: <${file.contentId ?? ''}>`,
        `Content-Disposition: inline; filename="${name}"`,
        '',
        base64Lines(file.bytes),
      );
    }
    content.push(`--${related}--`);
    contentType = `multipart/related; boundary="${related}"`;
  }

  if (attached.length === 0) {
    return [...headers, `Content-Type: ${contentType}`, '', ...content, ''].join('\r\n');
  }

  const mixed = `000000000000${Math.random().toString(16).slice(2, 14)}`;
  const lines = [
    ...headers,
    `Content-Type: multipart/mixed; boundary="${mixed}"`,
    '',
    `--${mixed}`,
    `Content-Type: ${contentType}`,
    '',
    ...content,
  ];
  for (const file of attached) {
    const name = safeName(file);
    lines.push(
      `--${mixed}`,
      `Content-Type: ${file.mimeType || 'application/octet-stream'}; name="${name}"`,
      'Content-Transfer-Encoding: base64',
      `Content-Disposition: attachment; filename="${name}"`,
      '',
      base64Lines(file.bytes),
    );
  }
  lines.push(`--${mixed}--`, '');
  return lines.join('\r\n');
}

/** The message as Gmail's `raw` field wants it: base64url of the RFC 2822 text. */
export function buildMime(parts: MailParts): string {
  return base64url(new TextEncoder().encode(buildRfc822(parts)));
}

/** Base64 in 76-character lines, as RFC 2045 requires inside a message body. */
export function base64Lines(bytes: Uint8Array): string {
  return base64(bytes)
    .replace(/(.{76})/g, '$1\r\n')
    .replace(/\r\n$/, '');
}

/** RFC 2047 for a header that is not plain ASCII; untouched when it is. */
export function encodeHeader(value: string): string {
  if (/^[\x20-\x7e]*$/.test(value)) return value;
  return `=?UTF-8?B?${base64(new TextEncoder().encode(value))}?=`;
}

/**
 * Quoted-printable, which is what Gmail uses for its own parts. Keeps the
 * message readable in a raw view — the way Gmail's are — where base64 bodies
 * are not.
 */
export function quotedPrintable(text: string): string {
  const bytes = new TextEncoder().encode(text.replace(/\r?\n/g, '\r\n'));
  let out = '';
  let lineLength = 0;
  for (const byte of bytes) {
    let chunk: string;
    if (byte === 13) {
      // Half of a CRLF; the LF that follows writes both.
      continue;
    } else if (byte === 10) {
      // A space at the end of a line is not allowed to be bare in QP.
      if (out.endsWith(' ')) out = `${out.slice(0, -1)}=20`;
      out += '\r\n';
      lineLength = 0;
      continue;
    } else if ((byte >= 33 && byte <= 126 && byte !== 61) || byte === 32) {
      chunk = String.fromCharCode(byte);
    } else {
      chunk = `=${byte.toString(16).toUpperCase().padStart(2, '0')}`;
    }
    if (lineLength + chunk.length > 75) {
      out += '=\r\n';
      lineLength = 0;
    }
    out += chunk;
    lineLength += chunk.length;
  }
  return out;
}

function base64(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

function base64url(bytes: Uint8Array): string {
  return base64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// ── end shared mail-send rules ──
